import { useState } from "react";
import { createPortal } from "react-dom";
import { TbChevronDown } from "react-icons/tb";
import {
  PORTAL_DROPDOWN_BACKDROP_Z_INDEX,
  PORTAL_DROPDOWN_Z_INDEX,
  usePortalDropdownPosition,
} from "../../../../Hooks/usePortalDropdownPosition";
import { PixKeySummary } from "./PixKeySummary";

const MENU_MAX_HEIGHT = 320;

/**
 * Custom Pix key dropdown — options mirror Minhas chaves list rows (not a native select).
 */
export const PixKeySelect = ({
  options = [],
  value = "",
  onChange,
  disabled = false,
  placeholder = "Selecione uma chave Pix",
  id = "pix-key-select",
  "aria-label": ariaLabel = "Chave Pix",
  className = "",
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const { triggerRef, menuRef, menuStyle } = usePortalDropdownPosition(open, MENU_MAX_HEIGHT);

  const selected = options.find((item) => item.id === value) || null;

  const handleClose = () => setOpen(false);

  const handleSelect = (keyId) => {
    onChange?.(keyId);
    setOpen(false);
  };

  return (
    <div className={className}>
      <div className="relative">
        <button
          ref={triggerRef}
          id={id}
          type="button"
          disabled={disabled || options.length === 0}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
          onClick={() => setOpen((prev) => !prev)}
          className={`flex w-full items-center gap-2 rounded-xl border bg-white text-left transition ${
            compact ? "px-2.5 py-2" : "px-3 py-3"
          } ${
            open
              ? "border-[#e7a73f] ring-1 ring-[#e7a73f]/40"
              : "border-gray-200 hover:border-gray-300"
          } disabled:cursor-not-allowed disabled:opacity-60`}
        >
          <span className="min-w-0 flex-1">
            {selected ? (
              <PixKeySummary
                item={selected}
                compact={compact}
                showStatus={!compact}
                iconClassName={compact ? "h-8 w-8" : "h-11 w-11"}
              />
            ) : (
              <span className={`block text-sm text-gray-400 ${compact ? "py-1" : "py-2"}`}>
                {placeholder}
              </span>
            )}
          </span>
          <TbChevronDown
            className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
              open ? "rotate-180" : ""
            }`}
            aria-hidden
          />
        </button>

        {open &&
          menuStyle &&
          createPortal(
            <>
              <div
                className="fixed inset-0"
                style={{ zIndex: PORTAL_DROPDOWN_BACKDROP_Z_INDEX }}
                onClick={handleClose}
                aria-hidden="true"
              />
              <div
                ref={menuRef}
                role="listbox"
                aria-label={ariaLabel}
                style={{
                  ...menuStyle,
                  zIndex: PORTAL_DROPDOWN_Z_INDEX,
                  display: "flex",
                  flexDirection: "column",
                }}
                className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-card"
              >
                <div className="min-h-0 flex-1 overflow-y-auto py-1">
                  {options.map((item) => {
                    const isActive = item.id === value;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        onClick={() => handleSelect(item.id)}
                        className={`flex w-full border-b border-gray-50 px-3 py-3 text-left transition-colors last:border-b-0 ${
                          isActive
                            ? "bg-[#fff8ec] text-brand-dark"
                            : "text-brand-dark hover:bg-gray-50"
                        }`}
                      >
                        <PixKeySummary
                          item={item}
                          compact={compact}
                          showStatus={!compact}
                          className="w-full"
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            </>,
            document.body
          )}
      </div>
    </div>
  );
};
